<template>
  <div class="p-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-gray-700">
        {{ phoneNumbers.length }} phone number{{ phoneNumbers.length !== 1 ? 's' : '' }}
      </h3>
      <button
        class="text-xs text-gray-500 hover:text-gray-700"
        :disabled="loading"
        @click="fetchData"
      >
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" class="mr-1" />
        Refresh
      </button>
    </div>

    <div v-if="loading" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'circle-notch']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <div v-else-if="phoneNumbers.length === 0" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'phone']" class="text-3xl text-gray-300 mb-3" />
      <p class="text-sm text-gray-500">No phone numbers configured</p>
    </div>

    <div v-else class="header-wrap-table">
      <AgGridVue
        :theme="standardListTheme"
        style="height: 500px"
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :row-data="phoneNumbers"
        @grid-ready="onGridReady"
      />
    </div>

    <div v-if="error" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { standardListTheme } from '@/utils/common'
import { listTenantPhoneNumbers, assignUser } from '@/services/api/tenantPhoneNumbers'
import { listUsers } from '@/services/api/adminUsers'

export default {
  name: 'TelephonyPhoneNumbers_Admin',
  components: { AgGridVue, FontAwesomeIcon },

  data() {
    return {
      phoneNumbers: [],
      users: [],
      loading: true,
      error: null,
      gridApi: null,
      standardListTheme,
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMovable: true,
      },
    }
  },

  computed: {
    userOptions() {
      return [
        { label: 'Unassigned', value: null },
        ...this.users.map((u) => ({
          label: `${u.first_name} ${u.last_name}`.trim() || u.email,
          value: u.id,
        })),
      ]
    },

    columnDefs() {
      const self = this
      return [
        {
          headerName: 'Phone Number',
          field: 'phone_number',
          cellClass: 'sensitive',
          valueFormatter: (params) => self.formatPhone(params.value),
          flex: 1,
          minWidth: 160,
        },
        {
          headerName: 'Friendly Name',
          field: 'friendly_name',
          flex: 1,
          minWidth: 150,
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 120,
          cellRenderer: (params) => {
            const status = params.value || 'unknown'
            const isActive = status === 'active'
            const badgeClass = isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-600'
            const el = document.createElement('span')
            el.className = `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`
            el.textContent = status
            return el
          },
        },
        {
          headerName: 'Assigned User',
          field: 'assigned_user',
          cellClass: 'sensitive',
          flex: 1,
          minWidth: 200,
          cellRenderer: (params) => {
            const container = document.createElement('div')
            container.className = 'flex items-center h-full'
            const select = document.createElement('select')
            select.className = 'text-sm border border-gray-300 rounded-md px-2 py-1 w-full'
            self.userOptions.forEach((opt) => {
              const option = document.createElement('option')
              option.value = opt.value === null ? '' : String(opt.value)
              option.textContent = opt.label
              if (opt.value === params.value) {
                option.selected = true
              }
              select.appendChild(option)
            })
            select.addEventListener('change', () => {
              const newVal = select.value === '' ? null : Number(select.value)
              self.handleAssign(params.data.id, newVal, params.node)
            })
            container.appendChild(select)
            return container
          },
        },
      ]
    },
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    onGridReady(params) {
      this.gridApi = params.api
    },

    async fetchData() {
      this.loading = true
      this.error = null
      try {
        const [phones, users] = await Promise.all([
          listTenantPhoneNumbers(),
          listUsers(),
        ])
        this.phoneNumbers = phones
        this.users = users
      } catch {
        this.error = 'Failed to load phone numbers'
      } finally {
        this.loading = false
      }
    },

    async handleAssign(phoneNumberId, userId, rowNode) {
      try {
        const updated = await assignUser(phoneNumberId, userId)
        rowNode.setData(updated)
      } catch {
        this.error = 'Failed to update assignment'
        this.fetchData()
      }
    },

    formatPhone(phone) {
      if (!phone) return ''
      const digits = phone.replace(/\D/g, '')
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      return phone
    },
  },
}
</script>
